import { useState } from "react";
import { events as analyticsEvents } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

function humanizeAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("invalid login credentials")) return "Email ou senha incorretos.";
  if (m.includes("email not confirmed")) return "Confirme seu email antes de entrar. Verifique sua caixa de entrada.";
  if (m.includes("user already registered")) return "Este email já está cadastrado. Tente entrar.";
  if (m.includes("rate limit")) return "Muitas tentativas. Aguarde um minuto e tente novamente.";
  if (m.includes("network")) return "Falha de rede. Verifique sua conexão.";
  return message;
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { SyraLogo } from "@/components/brand/SyraLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

type View = "auth" | "forgot";

export default function Login() {
  const { login, signup, loginWithGoogle, resetPassword } = useAuth();
  const { toast } = useToast();
  const [view, setView] = useState<View>("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: "Bem-vindo ao SYRA!" });
    } catch (err: any) {
      toast({
        title: "Não foi possível entrar",
        description: humanizeAuthError(err?.message ?? "Erro desconhecido"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Senha muito curta", description: "A senha deve ter pelo menos 6 caracteres.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, name);
      analyticsEvents.signupCompleted();
      // Detectar se auto-confirm está ativo: se já temos sessão, login é instantâneo.
      const { data: sess } = await supabase.auth.getSession();
      if (sess?.session) {
        toast({ title: "Conta criada!", description: "Bem-vindo ao SYRA. Levando você ao onboarding..." });
      } else {
        toast({
          title: "Verifique seu email",
          description: `Enviamos um link de confirmação para ${email}. Clique no link para ativar sua conta antes de entrar.`,
          duration: 8000,
        });
      }
    } catch (err: any) {
      toast({
        title: "Erro no cadastro",
        description: humanizeAuthError(err?.message ?? "Erro desconhecido"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      await loginWithGoogle();
    } catch (err: any) {
      toast({ title: "Erro com Google", description: err.message, variant: "destructive" });
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword(resetEmail);
      toast({ title: "Email enviado!", description: "Verifique sua caixa de entrada para redefinir a senha." });
      setView("auth");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const passwordField = (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Senha</label>
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
          className="h-11 bg-background/50 border-border/60 focus:border-primary/50 transition-colors pr-10"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const emailField = (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Email</label>
      <Input
        type="email"
        placeholder="seu@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="h-11 bg-background/50 border-border/60 focus:border-primary/50 transition-colors"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 dark:from-background dark:via-background dark:to-primary/5">
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }} className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.4 }} className="absolute top-4 right-4">
        <ThemeToggle />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      >
        <Card className="w-full max-w-lg relative z-10 border-border/50 bg-card/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_hsl(var(--primary)/0.12)] dark:shadow-[0_20px_60px_-15px_hsl(var(--primary)/0.15)] dark:bg-card/70">
          <CardHeader className="text-center space-y-5 px-8 pt-8 pb-4">
            <div className="mx-auto w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center shadow-[0_8px_24px_-4px_hsl(var(--primary)/0.35)]">
              <SyraLogo size={36} variant="mark" tone="mono-light" />
            </div>
            <div className="space-y-1.5">
              <CardTitle className="text-3xl font-headline tracking-tight">SYRA</CardTitle>
              <CardDescription className="text-muted-foreground text-sm">
                Gestor Inteligente de Grupos WhatsApp
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8 pt-2">
            {view === "forgot" ? (
              <form onSubmit={handleForgot} className="space-y-5">
                <button type="button" onClick={() => setView("auth")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Voltar ao login
                </button>
                <p className="text-sm text-muted-foreground">Informe seu email para receber o link de redefinição de senha.</p>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="h-11 bg-background/50 border-border/60 focus:border-primary/50 transition-colors"
                  />
                </div>
                <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enviar Link
                </Button>
              </form>
            ) : (
              <div className="space-y-5">
                <Button variant="outline" className="w-full h-11 gap-2" onClick={handleGoogle}>
                  <GoogleIcon /> Continuar com Google
                </Button>

                <div className="flex items-center gap-3">
                  <Separator className="flex-1" />
                  <span className="text-xs text-muted-foreground">ou</span>
                  <Separator className="flex-1" />
                </div>

                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="login">Entrar</TabsTrigger>
                    <TabsTrigger value="signup">Criar Conta</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="mt-4">
                    <form onSubmit={handleLogin} className="space-y-4">
                      {emailField}
                      {passwordField}
                      <div className="text-right">
                        <button type="button" onClick={() => setView("forgot")} className="text-xs text-primary hover:underline">
                          Esqueci minha senha
                        </button>
                      </div>
                      <Button type="submit" className="w-full h-11 font-semibold shadow-md hover:shadow-lg transition-all duration-200" disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Entrar
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="signup" className="mt-4">
                    <form onSubmit={handleSignup} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">Nome</label>
                        <Input
                          type="text"
                          placeholder="Seu nome"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="h-11 bg-background/50 border-border/60 focus:border-primary/50 transition-colors"
                        />
                      </div>
                      {emailField}
                      {passwordField}
                      <Button type="submit" className="w-full h-11 font-semibold shadow-md hover:shadow-lg transition-all duration-200" disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Criar Conta
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Ao criar uma conta, você confirma que verificará seu email.
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            )}
            <p className="text-center text-xs text-muted-foreground mt-6">
              © {new Date().getFullYear()} SYRA · Todos os direitos reservados
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
